import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { BackLink, Button, Typography } from '../../components/atoms';
import {
  Add as AddIcon,
  Location as LocationIcon,
  Profile as ProfileIcon,
} from '../../components/icons';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import {
  AboutMyDataDialog,
  CampaignConsentDialog,
} from '../../components/partials';
import * as Api from '../../weborder/Api';
import { DispatchProps, StateProps } from '../../weborder/component/Delivery';
import { Utilities } from '../../weborder/Utilities';
import { domUtils } from '../../weborder/utils';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  aboutMyData: {
    paddingRight: theme.spacing(3),
    textAlign: 'right',
  },

  deliveryNavList: {
    marginBottom: theme.spacing(3),
  },

  locationItem: {
    padding: theme.spacing(0, 2),

    '& .secondary': {
      color: theme.palette.grey[800],
    },
  },

  [theme.breakpoints.up('md')]: {
    aboutMyData: {
      paddingRight: 0,
    },
  },
}));

export function DeliverySelectLocation(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    AppConfig: appConfig,
    Customer: customer,
    DeliveryLocations: customerDeliveryLocations,
    deliveryOptions: deliveryRestaurants,
    isLoggedIn,
    LastRestaurantUrl: lastRestaurantUrl,
    PushLoginUrl: pushLoginUrl,
    translate,
  } = props;

  const [showAboutMyData, setShowAboutMyData] = useState(false);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    pushLoginUrl();
  }, []);

  useEffect(() => {
    if (appConfig.status === 'success' && !appConfig.data.DeliveryEnabled) {
      Utilities.navigateTo('/where');
    }
  }, [appConfig.status]);

  if (
    appConfig.status !== 'success' ||
    customerDeliveryLocations.status !== 'success'
  ) {
    return <LoadingSpinner />;
  }

  const getLocationAddress = (deliveryLocation: Api.DeliveryLocation) => {
    if (
      appConfig.data.DeliveryAddressLayout ===
      Api.AddressLayoutType.FullStreetAddressFirst
    ) {
      return (
        <div className={classes.locationItem}>
          <Typography>
            <em>
              {`${deliveryLocation.Street} , `}
              {deliveryLocation.Building ? deliveryLocation.Building : null}
            </em>
          </Typography>

          <Typography variant="body2" className="secondary">
            {deliveryLocation.Town}
            {deliveryLocation.PostCode
              ? `, ${deliveryLocation.PostCode}`
              : null}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.locationItem}>
        <Typography>
          <em>
            {deliveryLocation.Building ? `${deliveryLocation.Building}` : ''}{' '}
            {`${deliveryLocation.Street}`}
          </em>
        </Typography>
        <Typography variant="body2" className="secondary">
          {deliveryLocation.Town}
        </Typography>
      </div>
    );
  };

  const getDeliveryLocationUrl = (deliveryLocation: Api.DeliveryLocation) => {
    const option = deliveryRestaurants[deliveryLocation.DeliveryLocationId];

    if (!option || option.status !== 'success') {
      return `/delivery/${deliveryLocation.DeliveryLocationId}`;
    }

    switch (option.data.type) {
      case 'choice':
        return `/delivery/${deliveryLocation.DeliveryLocationId}`;

      case 'skip':
        return `/restaurant/${option.data.to.VirtualRestaurantId}/delivery/${deliveryLocation.DeliveryLocationId}`;
    }
  };

  const getLocationLoadingItem = () => {
    return {
      elements: [
        <LocationIcon key="item-location-loading-icon" />,
        <>
          <Typography
            variant="body2"
            key="item-location-loading-text"
            className="secondary"
          >
            <em>{translate('Loading_delivery_options')}</em>
          </Typography>
        </>,
      ],
      key: 'address-loading',
      linkId: 'address-loading',
      variant: 'noEndIcon',
    };
  };

  const getLocationItem = (deliveryLocation: Api.DeliveryLocation) => {
    return {
      elements: [
        <LocationIcon
          key={`delivery-icon-${deliveryLocation.DeliveryLocationId}`}
        />,
        getLocationAddress(deliveryLocation),
      ],
      key: `item-user-location-${deliveryLocation.DeliveryLocationId}`,
      linkId: `item-user-location-${deliveryLocation.DeliveryLocationId}`,
      linkPath: getDeliveryLocationUrl(deliveryLocation),
    };
  };

  const getNewLocationItem = () => {
    return {
      elements: [
        <AddIcon key="new-location-icon" />,
        <Typography key="new-location-label" className={classes.locationItem}>
          <em>{translate('Delivery_button_newLocation')}</em>
        </Typography>,
      ],
      key: 'item-new-location',
      linkId: 'add-new-address',
      linkPath: '/delivery/new',
      variant: 'noEndIcon',
    };
  };

  const getLogInItem = () => {
    return {
      elements: [
        <ProfileIcon key="login-icon" />,
        <div key="login-container" className={classes.locationItem}>
          <Typography key="login-label">
            <em>{translate('Login')}</em>
          </Typography>
          <Typography
            key="login-description"
            variant="body2"
            className="secondary"
          >
            {translate('Delivery_Button_Login')}
          </Typography>
        </div>,
      ],
      key: 'item-login',
      linkId: 'login',
      linkPath: '/phone',
      variant: 'noEndIcon',
    };
  };

  const getNavListItems = () => {
    const items = [];
    if (isLoggedIn) {
      if (!customerDeliveryLocations) {
        items.push(getLocationLoadingItem);
      } else {
        customerDeliveryLocations.data.forEach((location) =>
          items.push(getLocationItem(location)),
        );
      }
    }

    items.push(getNewLocationItem());

    if (!isLoggedIn) {
      items.push(getLogInItem());
    }

    return items;
  };

  const renderBackLink = (configData) => {
    if (configData.PickupEnabled) {
      return <BackLink label={translate('Back')} linkPath="/where" />;
    }

    if (lastRestaurantUrl !== '/') {
      return (
        <BackLink label={translate('Menu')} linkPath={lastRestaurantUrl} />
      );
    }

    return null;
  };

  const handleOnClickAboutMyData = () => {
    setShowAboutMyData(!showAboutMyData);
  };

  const locationOptions = getNavListItems();

  return (
    <>
      <Page pageName="delivery-select-location">
        <header>
          {renderBackLink(appConfig.data)}

          <Typography variant="h1">{translate('Deliver_to')}</Typography>
        </header>

        <article>
          <NavList
            items={locationOptions}
            className={classes.deliveryNavList}
          />

          <div className={classes.aboutMyData}>
            <Button
              className="noUnderline"
              onClick={handleOnClickAboutMyData}
              variant="text"
            >
              {translate('About_my_data')}
            </Button>
          </div>
        </article>
      </Page>

      {showAboutMyData && (
        <AboutMyDataDialog
          handleOnClick={handleOnClickAboutMyData}
          showDialog={showAboutMyData}
          translate={translate}
        />
      )}

      {customer.status === 'success' && !!customer.data.EmailAddress && (
        <CampaignConsentDialog customer={customer} />
      )}
    </>
  );
}
